import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 25000, withCredentials: true });

const TOKEN_KEY = "cot_token";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const MAX_AUTH_RETRIES = 3;
// A transient failure has NO HTTP response (timeout / network / connection reset).
// These must NEVER be treated as "logged out".
const isTransient = (err) => !err.response;

// --- Heavy-load guard -------------------------------------------------------
// The dashboard mounts ~15-20 panels that each fire a request on load. Hitting the
// single backend worker with all of them at once floods the event loop and slows
// first paint (and used to starve auth). Gate concurrent requests to a small number
// so panels stream in smoothly. Auth calls (_retryAuth) bypass the gate entirely.
const MAX_CONCURRENT = 5;
let _active = 0;
const _waiters = [];

function acquireSlot() {
  if (_active < MAX_CONCURRENT) {
    _active += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => _waiters.push(resolve));
}

function releaseSlot() {
  const next = _waiters.shift();
  if (next) next(); // hand the slot directly to the next waiter (_active unchanged)
  else _active = Math.max(0, _active - 1);
}

// Send the JWT as a Bearer header so auth works cross-origin (production), where the
// httpOnly SameSite=None cookie would be a blocked third-party cookie.
client.interceptors.request.use(async (config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  // Auth requests skip the gate so login/session checks are never queued.
  if (!config._retryAuth && !config._gated) {
    await acquireSlot();
    config._gated = true;
  }
  return config;
});

client.interceptors.response.use(
  (r) => {
    if (r.config?._gated) {
      releaseSlot();
      r.config._gated = false;
    }
    return r;
  },
  async (err) => {
    const config = err.config || {};
    if (config._gated) {
      releaseSlot();
      config._gated = false;
    }
    const status = err.response?.status;
    const isLogin = (config.url || "").replace(/^\//, "") === "auth/login";

    // Only a GENUINE 401 (and not on the login attempt itself) means logged out.
    if (status === 401 && !isLogin) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem("cot_user");
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      return Promise.reject(err);
    }

    // Transient failure on a retry-flagged auth request → retry with backoff.
    // The token is preserved; a brief backend stall no longer logs the user out.
    if (config._retryAuth && isTransient(err)) {
      config._retryCount = (config._retryCount || 0) + 1;
      if (config._retryCount <= MAX_AUTH_RETRIES) {
        await sleep(400 * 2 ** (config._retryCount - 1)); // 400, 800, 1600ms
        return client.request(config);
      }
    }
    return Promise.reject(err);
  }
);

export const login = async (email, password) => {
  const { data } = await client.post("/auth/login", { email, password }, { _retryAuth: true });
  return data;
};

export const logout = async () => {
  try {
    await client.post("/auth/logout");
  } catch {
    /* ignore — cookie clear is best-effort */
  }
};

export const fetchMe = async () => {
  const { data } = await client.get("/auth/me", { _retryAuth: true });
  return data;
};

export const fetchUsers = async () => {
  const { data } = await client.get("/auth/users");
  return data;
};

export const createUser = async (payload) => {
  const { data } = await client.post("/auth/users", payload);
  return data;
};

export const deleteUser = async (email) => {
  const { data } = await client.delete(`/auth/users/${encodeURIComponent(email)}`);
  return data;
};

export const changeCredentials = async (payload) => {
  const { data } = await client.post("/auth/change-credentials", payload);
  return data;
};

export const fetchSnapshot = async () => {
  const { data } = await client.get("/cot/snapshot");
  return data;
};

export const fetchSummary = async () => {
  const { data } = await client.get("/cot/summary");
  return data;
};

export const fetchHistory = async (code, weeks = 52) => {
  const { data } = await client.get(`/cot/history/${code}`, {
    params: { weeks },
  });
  return data;
};

export const fetchMarkets = async () => {
  const { data } = await client.get("/cot/markets");
  return data;
};

export const fetchEU10Y = async (days = 130) => {
  const { data } = await client.get("/yield/eu10y", { params: { days } });
  return data;
};

export const fetchUSRealYields = async (days = 180) => {
  const { data } = await client.get("/yield/us-real", { params: { days }, timeout: 40000 });
  return data;
};

export const fetchRealSpreadOverlay = async (days = 180) => {
  const { data } = await client.get("/yield/real-spread", { params: { days }, timeout: 40000 });
  return data;
};

export const fetchTopHeadlines = async (refresh = false) => {
  const { data } = await client.get("/news/top-headlines", {
    params: refresh ? { refresh: true } : {},
    timeout: 45000,
  });
  return data;
};

export const fetchYieldComparison = async (refresh = false) => {
  const { data } = await client.get("/yield/comparison", {
    params: refresh ? { refresh: true } : {},
    timeout: 40000,
  });
  return data;
};

export const fetchYieldComparisonAI = async () => {
  const { data } = await client.get("/yield/comparison/ai", { timeout: 45000 });
  return data;
};

export const fetchRateDifferentials = async () => {
  const { data } = await client.get("/yield/differentials", { timeout: 40000 });
  return data;
};

export const fetchCotRead = async (refresh = false) => {
  const { data } = await client.get("/cot/read", {
    timeout: 45000,
    params: refresh ? { refresh: true } : {},
  });
  return data;
};

export const fetchCarry = async () => {
  const { data } = await client.get("/yield/carry", { timeout: 40000 });
  return data;
};

export const fetchCorrelation = async () => {
  const { data } = await client.get("/cross/correlation", { timeout: 40000 });
  return data;
};

export const fetchVIX = async (days = 130) => {
  const { data } = await client.get("/vix/index", { params: { days } });
  return data;
};

export const fetchUSDBias = async () => {
  const { data } = await client.get("/news/bias", { params: { currency: "USD" } });
  return data;
};

export const fetchBias = async (currency = "USD", refresh = false) => {
  const { data } = await client.get("/news/bias", {
    params: refresh ? { currency, refresh: true } : { currency },
    timeout: 45000,
  });
  return data;
};

export const fetchMacro = async (code) => {
  const { data } = await client.get(`/macro/${code}`);
  return data;
};

export const fetchInflation = async () => {
  const { data } = await client.get("/inflation");
  return data;
};

export const fetchInflationRead = async (refresh = false) => {
  const { data } = await client.get("/inflation/read", {
    params: refresh ? { refresh: true } : {},
    timeout: refresh ? 90000 : 45000,
  });
  return data;
};

export const fetchDxyWrap = async (refresh = false, date = null) => {
  const params = {};
  if (refresh) params.refresh = true;
  if (date) params.date = date;
  const { data } = await client.get("/dxy/session-wrap", {
    params,
    timeout: refresh ? 90000 : 45000,
  });
  return data;
};

export const fetchDxyWrapArchive = async () => {
  const { data } = await client.get("/dxy/wraps");
  return data;
};

export const fetchDxyTicker = async () => {
  const { data } = await client.get("/dxy/ticker");
  return data;
};

export const fetchDxyCloseHistory = async (days = 15, instrument = "DXY") => {
  const { data } = await client.get("/dxy/dxy-history", { params: { days, instrument } });
  return data;
};

export const fetchRiskTone = async () => {
  const { data } = await client.get("/macro/risk-tone", { timeout: 20000 });
  return data;
};

export const fetchInflationCommentary = async (code) => {
  const { data } = await client.get(`/inflation/commentary/${code}`);
  return data;
};

export const fetchInflationExpectations = async (refresh = false) => {
  const { data } = await client.get("/inflation/expectations", {
    params: refresh ? { refresh: true } : {},
    timeout: 45000,
  });
  return data;
};

export const fetchMacroBrief = async (a, b, refresh = false) => {
  const { data } = await client.get("/inflation/brief", {
    params: refresh ? { a, b, refresh: true } : { a, b },
    timeout: 45000,
  });
  return data;
};

export const fetchTrackedSetups = async () => {
  const { data } = await client.get("/setups");
  return data;
};

export const trackSetup = async (payload) => {
  const { data } = await client.post("/setups", payload);
  return data;
};

export const untrackSetup = async (id) => {
  const { data } = await client.delete(`/setups/${id}`);
  return data;
};

export const resolveSetup = async (id, status) => {
  const { data } = await client.patch(`/setups/${id}`, null, { params: { status } });
  return data;
};

export const checkSetups = async () => {
  const { data } = await client.get("/setups/check", { timeout: 20000 });
  return data;
};

export const fetchWatchlist = async () => {
  const { data } = await client.get("/watchlist");
  return data;
};

export const addWatchlist = async (payload) => {
  const { data } = await client.post("/watchlist", payload);
  return data;
};

export const removeWatchlist = async (code) => {
  const { data } = await client.delete(`/watchlist/${code}`);
  return data;
};

export const fetchLlmUsage = async () => {
  const { data } = await client.get("/llm/usage");
  return data;
};

export const fetchIsmPmi = async () => {
  const { data } = await client.get("/ism/pmi");
  return data;
};

export const fetchNfp = async () => {
  const { data } = await client.get("/nfp");
  return data;
};

export const fetchNfpDetail = async (index = null) => {
  const params = index == null ? {} : { index };
  const { data } = await client.get("/nfp/detail", { params, timeout: 45000 });
  return data;
};

export const fetchNfpMonthRead = async (index = null) => {
  const params = index == null ? {} : { index };
  const { data } = await client.get("/nfp/read", { params, timeout: 60000 });
  return data;
};

export const fetchEconCalendar = async (currencies, impact) => {
  const params = {};
  if (currencies) params.currencies = currencies;
  if (impact) params.impact = impact;
  const { data } = await client.get("/calendar/economic", { params });
  return data;
};

export const fetchIsmRead = async (refresh = false) => {
  const { data } = await client.get("/ism/read", { params: { refresh } });
  return data;
};

export const fetchDataHealth = async () => {
  const { data } = await client.get("/health/data", { timeout: 15000 });
  return data;
};
